import { setHours, startOfDay } from "date-fns";
import { useState } from "react";
import ReactDatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { Button, Form, Icon, Input, Modal } from "semantic-ui-react";

import { DATE_FORMATS_FNS } from "components/DatePicker";
import "components/DBB/components/DBBAppointmentDateModal/DBBAppointmentDateModal.scss";
import { useDBBAppointmentDate } from "components/DBB/hooks";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { addDaysToDate, formatDate } from "utils/dateHelperFunctions";

import type { Appointment } from "models";

type DBBAppointmentDateModalProps = {
  appointment: Appointment;
  open: boolean;
  onClose: () => void;
};

export const DBBAppointmentDateModal = ({ appointment, open, onClose }: DBBAppointmentDateModalProps) => {
  const { t } = useTranslation();

  const tomorrowDate = addDaysToDate(new Date(), 1);

  const [contractDate, setContractDate] = useState<Date | null>(setHours(startOfDay(tomorrowDate), 8));

  const appointmentLocation = useAppointmentLocation(appointment);
  const { mutate: updateDBBAppointmentDate, isPending } = useDBBAppointmentDate();

  const handleSave = () => {
    if (!contractDate) return;

    updateDBBAppointmentDate({ appointment_id: appointment.id, dbb_appointment_date: formatDate(contractDate, DATE_FORMATS_FNS.ISOFormat) }, { onSuccess: onClose });
  };

  return (
    <Modal size="tiny" open={open} onClose={onClose} closeOnDimmerClick={false} className="DBBAppointmentDateModal">
      <Modal.Header>{t("v8_add_dbb_appointment", "Add DBB appointment")}</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field>
            <label>{t("v8_delivery_location", "Delivery location")}</label>
            <Input value={appointmentLocation?.dbb_location_id ?? ""} readOnly disabled />
          </Form.Field>
          <Form.Field>
            <label>{t("v8_delivery_date", "Delivery date")}</label>
            <ReactDatePicker
              selected={contractDate}
              onChange={setContractDate}
              minDate={tomorrowDate}
              showTimeInput
              showYearDropdown
              showMonthDropdown
              dateFormat={DATE_FORMATS_FNS.dateMonthYearTime}
              placeholderText={t("v8_choose_contract_date", "Choose contract date")}
            />
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button className="light" onClick={onClose}>
          {t("v8_cancel", "Cancel")}
          <Icon className="xmark" />
        </Button>
        <Button color="green" onClick={handleSave} disabled={isPending || !contractDate} loading={isPending}>
          {t("v8_save", "Save")}
          <Icon className="check" />
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
